// The move currently sliding across the board, if any. The scene starts one
// per move and asks for its frame each tick; it clears itself when done.
import { prefersReducedMotion } from '@bn-games/render-helpers';
import type { Color } from '../game/types.js';
import type { ViewerRotation } from '../render/geometry.js';
import { frameAt, planMoveAnimation, type MoveAnimationPlan } from '../render/PieceAnimator.js';
import type { MatchMove } from './Match.js';
import type { FrameInputs } from './frameModel.js';

export class SceneAnimation {
  private current: { readonly plan: MoveAnimationPlan; readonly startedAt: number; readonly turnBefore: Color } | null = null;

  get active(): boolean {
    return this.current !== null;
  }

  /** Side that made the move in flight — hot-seat keeps the board facing it until the move lands. */
  get turnBefore(): Color | null {
    return this.current?.turnBefore ?? null;
  }

  start(played: MatchMove, now: number): void {
    if (prefersReducedMotion()) return;
    this.current = { plan: planMoveAnimation(played.move, played.before.board), startedAt: now, turnBefore: played.before.turn };
  }

  clear(): void {
    this.current = null;
  }

  /** This tick's frame, or null once the move has landed. */
  frame(now: number, rotation: ViewerRotation): FrameInputs['animation'] {
    if (this.current === null) return null;
    const frame = frameAt(this.current.plan, now - this.current.startedAt, rotation);
    if (!frame.done) return { plan: this.current.plan, frame };
    this.current = null;
    return null;
  }
}
